// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useState } from "react";
import { App, Button, Space, Tag, Typography } from "antd";
import { forgetWebauthn, isWebauthnEnabled, registerWebauthn, unlockWebauthn, webauthnSupported } from "../webauthn";
import { resealUser, setSealMode, setWebauthnKey } from "../keystore";

// WebAuthnPanel binds (or unbinds) this browser's at-rest device-key seal to a hardware security key
// or platform biometric (WebAuthn PRF). Enabling re-seals every slot under the authenticator-derived
// key; disabling re-seals them back under the automatic browser key. A tap is required each time.
export function WebAuthnPanel({ userId }: { readonly userId: string }) {
  const { message } = App.useApp();
  const [enabled, setEnabled] = useState(isWebauthnEnabled(userId));
  const [busy, setBusy] = useState(false);
  const supported = webauthnSupported();
  const username = userId.replace(/^@/, "").split(":")[0];

  async function enable() {
    setBusy(true);
    try {
      const key = await registerWebauthn(userId, username); // create + a verifying tap → PRF key
      setWebauthnKey(key);
      setSealMode("webauthn");
      await resealUser(userId); // open browser-sealed slots, re-seal under the security key
      setEnabled(true);
      message.success("Keys are now locked to your security key — tap to unlock each session");
    } catch (e) {
      setWebauthnKey(null);
      setSealMode("auto");
      message.error(`Couldn't enable: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setBusy(true);
    try {
      const key = await unlockWebauthn(userId); // tap → key to open the wa1 slots
      setWebauthnKey(key);
      setSealMode("auto");
      await resealUser(userId); // re-seal back under the automatic browser key
      setWebauthnKey(null);
      forgetWebauthn(userId);
      setEnabled(false);
      message.success("Security-key lock removed — keys stay encrypted at rest automatically");
    } catch (e) {
      message.error(`Couldn't remove: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (!supported) {
    return <Typography.Text type="secondary">This browser doesn't support security keys (WebAuthn).</Typography.Text>;
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Typography.Text>
        Status: {enabled ? <Tag color="green">locked to a security key</Tag> : <Tag>not set</Tag>}
      </Typography.Text>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Bind this browser's device keys to a hardware <strong>security key (YubiKey)</strong> or a platform{" "}
        <strong>biometric</strong> (fingerprint / Face / Windows Hello). Unlocking needs a tap each session, and the
        secret never leaves the authenticator — so even a rooted or stolen device can't read your keys. Requires a
        PRF-capable authenticator; recover via your key backup if you lose it.
      </Typography.Paragraph>
      {enabled ? (
        <Button danger onClick={disable} loading={busy} className="wa-disable">
          Remove security-key lock
        </Button>
      ) : (
        <Button type="primary" onClick={enable} loading={busy} className="wa-enable">
          Protect with a security key / biometric
        </Button>
      )}
    </Space>
  );
}
